import type { BigDecimal } from "@solomon-labs/bigdecimal";

/**
 * YaaS Types
 */

export type YaasIsoDateTime = string;
export type YaasMetadata = Record<string, unknown> | null;

export interface ProgramCenteredListResponse<T> {
    data: T[];
    total: number;
    limit: number;
    offset: number;
    page: number;
}

export type ProgramStatus = "Draft" | "Active" | "Paused" | "Ended";
export type ProgramRunType = "IssuerRun" | "ClientRun";
/**
 * Legacy presentation metadata used by `yaas-issuer` during its migration to
 * canonical Program Config V3. WU does not use this field for runtime meaning.
 */
export type ProgramParticipationModel = "ClientControlledPositions" | "ClientEndUserEnrollments";
export type ProgramAssignmentScope = "AllIssuerClients" | "WhitelistedIssuerClients";
export type JachinProgramSyncStatus = "Pending" | "Synced" | "Rejected";
export type RewardDistributionMode = "Automatic" | "Manual";

export type ProgramAudience = "end_company" | "end_recipient";

export type ProgramTargetScalar = string | number | boolean;
export type ProgramParticipationMode = "automatic" | "participant_opt_in";

export interface ProgramMetadataPredicate {
    path: string;
    operator: "in";
    values: ProgramTargetScalar[];
}

export interface ProgramTarget {
    companyTermsRequirement?: NonNullable<ProgramTarget["termsRequirement"]>;
    termsRequirement?: {
        mode: "ALL" | "ANY";
        termsUuids: string[];
        existingParticipants: "KEEP_EXISTING" | "REQUIRE_CURRENT";
    };
    /**
     * Absence preserves legacy relationship selection. With `includeMode:
     * "add"` it is an array and can be empty: no Recipient Type is selected.
     */
    audience?: ProgramAudience | ProgramAudience[];
    /**
     * How the Whitelist works. `"add"`: a selected Recipient Type is every
     * account of that type that passes its Terms; the Whitelist adds accounts
     * on top of it, skips Terms, and does not need its type selected. The
     * Blacklist wins over both. Absent (legacy): a type's Whitelist narrows
     * that type to its entries. The console writes `"add"` on every target
     * it saves.
     */
    includeMode?: "add";
    /**
     * How a targeted participant joins this Program. Older API responses can
     * omit this field; clients must read that as automatic participation.
     */
    participationMode?: ProgramParticipationMode;
    subject: "issuer_client";
    identityConstraints: ProgramMetadataPredicate[];
    attributeFilters: ProgramMetadataPredicate[];
    includedSubjectUuids: string[];
    /**
     * Issuer Clients never in the Program. With an explicit audience these are
     * the excluded companies, and need Company Recipient in the audience.
     */
    excludedSubjectUuids: string[];
    /**
     * Whitelisted End Recipient wallets. Never stored empty. Legacy targets
     * (no `includeMode`): needs End Recipient in the audience, and narrows
     * that side to these Participant Accounts. See `includeMode`.
     */
    includedParticipantAccountUuids?: string[];
    /**
     * Whitelisted companies: Issuer Clients outside the self-client, each with
     * every account it holds now or later. Never stored empty. Legacy targets
     * (no `includeMode`): needs Company Recipient in the audience, and this
     * or `includedCompanyAccountUuids` narrows the company side to those
     * accounts. See `includeMode`.
     */
    includedCompanyClientUuids?: string[];
    /** Included company wallets: single accounts under companies. See `includedCompanyClientUuids`. */
    includedCompanyAccountUuids?: string[];
    /** Any explicit audience. These Participant Accounts are never in the Program (Exclude wins). */
    excludedParticipantAccountUuids?: string[];
}

export type ProgramPayoutModel =
    | { type: "fixed_amount"; amountAtomic: string }
    | { type: "fixed_apy"; annualRateBps: number }
    | { type: "variable_rewards"; rewardPassThroughBps: number };

/**
 * A formula parameter's stored value: a percent as a decimal string, bps as an
 * integer, a rate feed or a Program as its uuid.
 */
export type ProgramFormulaParameterValue = string | number;

/** A formula yaas-api defines, chosen by id and version, with its parameter values. */
export interface ProgramFormulaSelection {
    formulaId: string;
    formulaVersion: number;
    parameters: Record<string, ProgramFormulaParameterValue>;
}

/** How a stored Rate is set: one Fixed APY in percent, e.g. "3.5", or a formula. */
export type ProgramRewardsRateSource =
    | { mode: "fixed_apy"; percent: string }
    | { mode: "formula"; formula: ProgramFormulaSelection };

/** How a stored Fixed Pool's amount is set, in the Payout Asset's smallest unit. */
export type ProgramRewardsPoolAmount =
    | {
          mode: "fixed_amount";
          amountAtomic: string;
          denomination: "payout_asset" | "usd";
          period: "per_payout" | "full_program";
      }
    | { mode: "formula"; formula: ProgramFormulaSelection };

/**
 * The Rewards Method yaas-api stores beside the Payout Model: what a Program
 * pays and how the amount is worked out. The Payout Model is stored alongside
 * whenever it can express the method, and is null only for a formula it
 * cannot (Benchmark Top-Up).
 */
export type ProgramRewardsMethod =
    | { type: "rate"; convention: "simple_apr"; rate: ProgramRewardsRateSource }
    | { type: "fixed_pool"; pool: ProgramRewardsPoolAmount }
    | { type: "action_reward" }
    | { type: "distribution_only"; allocations: { recipient: string; amountAtomic: string }[] };

export const PROGRAM_DISTRIBUTION_PERIOD_UNITS = {
    minutes: { label: "Minutes", seconds: 60 },
    hours: { label: "Hours", seconds: 3_600 },
    days: { label: "Days", seconds: 86_400 },
    weeks: { label: "Weeks", seconds: 7 * 86_400 },
    months: { label: "Months", seconds: 30 * 86_400 }
} as const;

export type ProgramDistributionPeriodUnit = keyof typeof PROGRAM_DISTRIBUTION_PERIOD_UNITS;

export function isProgramDistributionPeriodUnit(value: unknown): value is ProgramDistributionPeriodUnit {
    return typeof value === "string" && value in PROGRAM_DISTRIBUTION_PERIOD_UNITS;
}

export interface ProgramDistributionPeriod {
    length: number;
    unit: ProgramDistributionPeriodUnit;
}

/**
 * Settings saved before the consoles stopped sending it also carry a legacy
 * `gate: "automatic"` that nothing reads. Payout Mode is the Program's
 * `rewardDistributionMode`.
 */
export interface ProgramDistributionSettings {
    /** Process every completed period from the selected start. */
    catchUp?: boolean;
    duration:
        | { mode: "continuous"; startsAt: YaasIsoDateTime; endsAt: null }
        | { mode: "fixed"; startsAt: YaasIsoDateTime; endsAt: YaasIsoDateTime };
    distributionPeriod: ProgramDistributionPeriod;
    payoutTiming: "period_end";
}

export interface Program {
    uuid: string;
    issuerOrganizationUuid: string;
    issuerClientUuid: string | null;
    parentProgramUuid: string | null;
    runType: ProgramRunType;
    /** Compatibility-only presentation field. It is not Program Config truth. */
    participationModel?: ProgramParticipationModel;
    assignmentScope: ProgramAssignmentScope | null;
    status: ProgramStatus;
    rewardDistributionMode: RewardDistributionMode;
    latestConfigVersionNumber: number;
    target: ProgramTarget | null;
    payoutModel: ProgramPayoutModel | null;
    /** Null until an author saves through the Rewards Method; older APIs omit it. */
    rewardsMethod?: ProgramRewardsMethod | null;
    distributionSettings: ProgramDistributionSettings | null;
    pinnedPolicySetRevisionUuid: string | null;
    name: string;
    description: string | null;
    metadata: YaasMetadata;
    /** Optional during rolling deploys where an older API omits Jachin state. */
    jachinCampaignSyncStatus?: JachinProgramSyncStatus | null;
    jachinCampaignSyncError?: string | null;
    jachinCampaignStartsAt?: YaasIsoDateTime | null;
    jachinCampaignEndsAt?: YaasIsoDateTime | null;
    jachinCampaignSyncedAt?: YaasIsoDateTime | null;
    jachinRewardPolicySyncStatus?: JachinProgramSyncStatus | null;
    jachinRewardPolicySyncError?: string | null;
    jachinRewardPolicySyncedAt?: YaasIsoDateTime | null;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type ProgramAssignmentStatus = "Pending" | "Active" | "Disabled";

export interface ProgramAssignment {
    uuid: string;
    programUuid: string;
    issuerClientUuid: string;
    status: ProgramAssignmentStatus;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type SurfaceCategoryStatus = "Active" | "Inactive";
/** Legacy name retained for clients that still render participation metadata. */
export type SurfaceParticipationMode = ProgramParticipationModel;

export interface SurfaceCategory {
    uuid: string;
    slug: string;
    name: string;
    description: string | null;
    observationSources: string[];
    /** Compatibility-only presentation field for the legacy issuer console. */
    participationMode?: SurfaceParticipationMode;
    status: SurfaceCategoryStatus;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type SurfaceTypeStatus = "Active" | "Inactive";

export interface SurfaceType {
    uuid: string;
    surfaceCategoryUuid: string;
    slug: string;
    name: string;
    providerName: string | null;
    description: string | null;
    status: SurfaceTypeStatus;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

/**
 * Reserved Surface Category slugs that application code branches on across the
 * issuer console, client console, and yaas-api. These are contracts, not free
 * text: the master-admin catalog must keep them stable, because code dispatches
 * on the exact value (default selection, deprecated-category hiding, account
 * surface detection, retail enrollment lookups). Mirrors the seeded catalog in
 * yaas-api (scripts/seed-protocol-catalog.ts). New provider categories that no
 * code special-cases do not belong here.
 */
export const SurfaceCategorySlug = {
    Address: "address",
    Treasury: "treasury",
    LiquidityPool: "liquidity-pool",
    Neobank: "neobank"
} as const;

/**
 * Reserved Surface Type slugs that application code branches on. Same contract
 * rules as SurfaceCategorySlug.
 */
export const SurfaceTypeSlug = {
    WalletAccount: "wallet-account"
} as const;

/**
 * Surface Category metadata.namingStrategy values that application code
 * branches on. Same contract rules as SurfaceCategorySlug: "pool" switches
 * the issuer console to server-generated Surface names composed from the
 * resolved pool (mirrors POOL_NAMING_STRATEGY in yaas-api).
 */
export const SurfaceNamingStrategy = {
    Pool: "pool"
} as const;

export type SurfaceStatus = "Active" | "Inactive";
export type AssetStatus = "Active" | "Inactive";
export type JachinAssetRegistrationStatus = "Pending" | "Registered" | "Rejected";

export interface Asset {
    uuid: string;
    issuerOrganizationUuid: string;
    symbol: string;
    name: string;
    mintAddress: string;
    // Null only on rows created before decimals capture; create always sets it.
    decimals: number | null;
    status: AssetStatus;
    /**
     * Null for Assets created before Jachin registration was introduced.
     * Optional during rolling deploys where an older API may omit the fields.
     */
    jachinRegistrationStatus?: JachinAssetRegistrationStatus | null;
    jachinRegistrationError?: string | null;
    jachinRegisteredAt?: YaasIsoDateTime | null;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type AssetDisplay = Pick<Asset, "uuid" | "symbol" | "name" | "mintAddress" | "decimals" | "status">;

export interface Surface {
    uuid: string;
    issuerOrganizationUuid: string;
    surfaceCategoryUuid: string;
    surfaceTypeUuid: string;
    assetUuid: string | null;
    asset: AssetDisplay | null;
    name: string;
    description: string | null;
    observationSources: Record<string, string>;
    status: SurfaceStatus;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type ProgramSurfaceStatus = "Active" | "Disabled";

export interface ProgramSurface {
    uuid: string;
    programUuid: string;
    surfaceUuid: string;
    status: ProgramSurfaceStatus;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export const ParticipantAccountStatus = {
    Pending: "Pending",
    Active: "Active",
    Suspended: "Suspended",
    Blacklisted: "Blacklisted",
    Closed: "Closed"
} as const;

// eslint-disable-next-line no-redeclare -- intentional const + companion type pair
export type ParticipantAccountStatus = (typeof ParticipantAccountStatus)[keyof typeof ParticipantAccountStatus];

export interface ParticipantAccount {
    uuid: string;
    issuerClientUuid: string | null;
    address: string;
    rewardsDestinationAddress: string | null;
    externalAccountId: string | null;
    externalUserId: string | null;
    username: string | null;
    description: string | null;
    status: ParticipantAccountStatus;
    isRewardsEligible: boolean;
    totalPositionsValue: string;
    totalEarnedRewards: string;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type ProgramPositionStatus = "Pending" | "Active" | "Rejected" | "Suspended" | "Disabled" | "Hidden" | "Ended";

export type ProgramPositionSourceKind = "IssuerClientManaged" | "ClientApiReported";

export type ProgramPositionControlKind = "IssuerClient" | "ClientEndUser";

export interface ProgramPosition {
    programUuid?: string;
    programName?: string;
    surfaceUuid?: string;
    surfaceName?: string;
    uuid: string;
    programSurfaceUuid: string;
    participantAccountUuid: string;
    participantAccountAddress?: string | null;
    /** Issuer console only: the client whose account holds the position. */
    issuerClientUuid?: string | null;
    status: ProgramPositionStatus;
    totalRewards: string;
    externalPositionId: string | null;
    positionSourceKind: ProgramPositionSourceKind | null;
    positionControlKind: ProgramPositionControlKind | null;
    recipientKind?: "company_recipient" | "end_recipient" | null;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type PolicyCategory = "Compliance" | "Eligibility" | "Allocation" | "Scheduling" | "CapsAndLimits";

export type PolicyPermission = "Locked" | "Constrained" | "Delegated";
export type PolicySetBy = "Issuer" | "Client";
export type PolicyInstanceStatus = "Draft" | "Active" | "Disabled";
export type PolicyRuleType = string;

export interface PolicyInstance {
    uuid: string;
    programUuid: string | null;
    ruleType: PolicyRuleType;
    category: PolicyCategory;
    /** Compatibility-only presentation field. WU does not consume it. */
    participationModel?: ProgramParticipationModel;
    parameters: Record<string, unknown>;
    permission: PolicyPermission | null;
    constraintConfig: Record<string, unknown> | null;
    setBy: PolicySetBy;
    status: PolicyInstanceStatus;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type PolicyCatalogEntryStatus = "Active" | "Inactive";

export interface PolicyCatalogEntry {
    uuid: string;
    ruleType: PolicyRuleType;
    label: string;
    description: string;
    category: PolicyCategory;
    allowedPermissions: PolicyPermission[];
    requiredByDefault: boolean;
    defaultParameters: Record<string, unknown>;
    defaultPermission: PolicyPermission | null;
    /** Compatibility-only presentation field. WU does not consume it. */
    participationModels?: ProgramParticipationModel[] | null;
    status: PolicyCatalogEntryStatus;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export interface PolicyInstancePresentation extends PolicyInstance {
    programPolicyInstanceUuid: string | null;
    label: string;
    description: string;
    currentValue: string;
    permissionView: string;
    constraintSummary: string | null;
    readOnly: boolean;
    boundaryPolicyUuid: string | null;
}

export type ProgramPolicyInstanceStatus = "Active" | "Disabled";

export interface ProgramPolicyInstance {
    uuid: string;
    programUuid: string;
    policyInstanceUuid: string;
    status: ProgramPolicyInstanceStatus;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type FundingSourceOwnerType = "IssuerOrganization" | "IssuerClient";
export type FundingSourceManager = "Owner" | "Jachin";
export type FundingSourceStatus = "Active" | "Inactive";

export interface FundingSource {
    uuid: string;
    ownerType: FundingSourceOwnerType;
    issuerOrganizationUuid: string;
    issuerClientUuid: string | null;
    assetUuid: string;
    asset: AssetDisplay | null;
    label: string;
    address: string;
    /**
     * Who operates the account. `Jachin` is provisioned by the provider and its
     * asset, address and status are immutable to the owner; `Owner` is legacy
     * operator-entered data that the model no longer admits for an issuer
     * organization.
     *
     * The org API does send this, on every row and never null: the column is
     * `FieldVisibility.Public` on the `FundingSource` entity, `managedBy` is in
     * its `SerializationKeys`, and the issuer list route serializes through
     * `IFundingSource`, which declares it non-optional. An earlier version of
     * this doc claimed the opposite and cited an `IIssuerFundingSource`
     * interface and a `contractMapper`; neither exists in yaas-api.
     */
    managedBy: FundingSourceManager;
    sourceType: string | null;
    status: FundingSourceStatus;
    balance: string;
    lastDepositAt: YaasIsoDateTime | null;
    totalDeposited: string | null;
    totalDistributed: string | null;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type ProgramFundingSourceStatus = "Active" | "Disabled";

export interface ProgramFundingSource {
    uuid: string;
    programUuid: string;
    fundingSourceUuid: string;
    status: ProgramFundingSourceStatus;
    metadata: YaasMetadata;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type RewardTransactionStatus = "Finalized" | "Reversed";

export interface RewardTransaction {
    uuid: string;
    programUuid: string;
    programSurfaceUuid: string;
    programPositionUuid: string;
    participantAccountUuid: string;
    asset: string;
    amount: string;
    status: RewardTransactionStatus;
    claimTransactionSignature: string | null;
    claimedAt: YaasIsoDateTime | null;
    createdAt: YaasIsoDateTime;
    updatedAt: YaasIsoDateTime;
}

export type ProgramsResponse = ProgramCenteredListResponse<Program>;
export type ProgramAssignmentsResponse = ProgramCenteredListResponse<ProgramAssignment>;
export type SurfaceCategoriesResponse = ProgramCenteredListResponse<SurfaceCategory>;
export type SurfaceTypesResponse = ProgramCenteredListResponse<SurfaceType>;
export type AssetsResponse = ProgramCenteredListResponse<Asset>;
export type SurfacesResponse = ProgramCenteredListResponse<Surface>;
export type ProgramSurfacesResponse = ProgramCenteredListResponse<ProgramSurface>;
export type ParticipantAccountsResponse = ProgramCenteredListResponse<ParticipantAccount>;
export type ProgramPositionsResponse = ProgramCenteredListResponse<ProgramPosition>;
export type PolicyInstancesResponse = ProgramCenteredListResponse<PolicyInstance>;
export type PolicyCatalogEntriesResponse = ProgramCenteredListResponse<PolicyCatalogEntry>;
export type PolicyInstancePresentationsResponse = ProgramCenteredListResponse<PolicyInstancePresentation>;
export type ProgramPolicyInstancesResponse = ProgramCenteredListResponse<ProgramPolicyInstance>;
export type FundingSourcesResponse = ProgramCenteredListResponse<FundingSource>;
export type ProgramFundingSourcesResponse = ProgramCenteredListResponse<ProgramFundingSource>;
export type RewardTransactionsResponse = ProgramCenteredListResponse<RewardTransaction>;

/**
 * DeFi protocol information
 */
export interface YaasDefiProtocol {
    uuid: string;
    name: string;
    slug: string;
}

/**
 * Legacy protocol information returned by wallet position compatibility responses.
 */
export interface LegacyProtocolMinimal {
    uuid: string;
    name: string;
    slug: string;
    type: string;
}

/**
 * DeFi product information
 */
export interface YaasDefiProduct {
    uuid: string;
    name: string;
    slug: string;
}

/**
 * YaaS position from API
 */
export interface YaasPosition {
    id: number;
    /** Stable public identifier — the retail position-management
     *  endpoints key on this, not the numeric `id`. */
    uuid: string;
    holdingId: string | null;
    surface: {
        id: string | null;
        name: string;
        displayName: string;
        displayType: "DEX_LP" | "TREASURY" | "NEOBANK" | "LENDING";
        address: string;
        providerName: string;
        category: string | null;
        type: string | null;
    };
    amount: string;
    lpPosition?: YaasProgramLpPosition;
    totalRewards: string;
    status: string | null;
    rewardsDestinationAddress: string | null;
    splits?: YaasPositionSplit[];
    /** How this position came to be enrolled, or `null` while it is
     *  still Pending and awaiting the participant's opt-in. Only the
     *  retail wallet dashboard emits this — optional because payloads
     *  cached before the field shipped will not carry it. */
    enrollmentSource?: RetailEnrollmentSource;
    recipientKind?: "company_recipient" | "end_recipient" | null;
    /** The Program behind this position. Needed to tell an
     *  `automatic` position (already earning) apart from a
     *  `participant_opt_in` one (needs an explicit enroll call). */
    program?: YaasPositionProgram;
    createdAt: string;
    updatedAt: string;
}

/** Exact decimal strings on the JSON boundary; use BigDecimal for calculations. */
export interface YaasHoldingObservation {
    amount: string | null;
    sourceComplete: boolean;
    solanaSlot: string | null;
}

/** Observed principal, independent of enrollment and reward lifecycle. */
export interface YaasLpHolding extends YaasLpPositionIdentity, YaasHoldingObservation {}

export interface YaasTokenHolding extends YaasHoldingObservation {
    kind: "token";
    assetMint: string;
}

/** Local LP projection of the wallet view, not a separate API endpoint. */
export interface YaasLpHoldingsSnapshot<T extends YaasLpHolding = YaasLpHolding> {
    walletAddress: string;
    configVersion: string;
    poolAddresses: string[];
    holdings: T[];
    complete: boolean;
    incompleteReason: "owner_enumeration_unavailable" | null;
    fetchedAt: string;
}

export interface YaasLpPositionIdentity {
    /** AMM protocol slug, passed through from Jachin unfiltered, e.g. "raydium-clmm" or "raydium-cpmm". */
    protocol: string;
    poolAddress: string;
    positionAddress: string;
    observedAssetMint: string;
    /** Pool pair such as "GOLD / USDv", computed by yaas-api from Jachin's
     *  surface catalog. Absent when the API cannot name the pool; callers
     *  then show `lpProtocolLabel(protocol)`. The frontends carry no pool addresses. */
    pairLabel?: string;
}

/**
 * The last positive observation yaas-api persisted for an LP Program
 * Position: both pool legs in raw atomic units at `solanaSlot`. Written
 * only when the LP is observed and never cleared, so it can be stale.
 * A zero leg is a real observation of zero principal, not a closure.
 */
export interface YaasLpTokenObservation {
    tokenX: string;
    tokenY: string;
    amountX: string;
    amountY: string;
    solanaSlot: string | null;
    observedAt: string | null;
    fetchedAt: string;
}

/** An LP Program Position's identity, plus its last observation once one exists. */
export type YaasProgramLpPosition = YaasLpPositionIdentity & (YaasLpTokenObservation | Record<never, never>);

/**
 * Retail participation vocabulary, as returned by the public wallet
 * dashboard. Deliberately distinct from `ProgramParticipationModel`
 * (`ClientControlledPositions` / `ClientEndUserEnrollments`), which is
 * the issuer/client console's view of the same concept.
 */
export type RetailParticipationMode = "automatic" | "participant_opt_in";

/** `null` on a Pending position that nobody has enrolled yet. */
export type RetailEnrollmentSource = RetailParticipationMode | null;

/** Program summary carried on each retail wallet-dashboard position. */
export interface YaasPositionProgram {
    uuid: string;
    name: string;
    status: ProgramStatus;
    participationMode: RetailParticipationMode;
}

export interface YaasPositionSplit {
    savedDestinationUuid: string;
    name: string;
    address: string;
    percentageBps: number;
}

export interface YaasSavedDestination {
    uuid: string;
    name: string;
    address: string;
    createdAt: string;
    updatedAt: string;
}

/**
 * Participant account summary from wallet API
 */
export interface WalletParticipantAccount {
    label?: string | null;
    uuid: string;
    address: string;
    rewardsDestinationAddress: string | null;
    status: ParticipantAccountStatus;
    /** Account-level payout mode; null for accounts provisioned
     *  before the mode existed. */
    distributionMode: "stream" | "claim" | null;
    /** Enrollment responses supply this; wallet summaries can omit it. */
    isRewardsEligible?: boolean;
    totalPositionsValue: string;
    totalEarnedRewards: string;
    /** Lifetime paid annual return over completed settlements; no compounding. */
    historicalAprPct?: string | null;
    createdAt: string;
    updatedAt: string;
}

/** Exact public contract for Solomon Main App retail enrollment. */
export interface RetailEnrollmentPayload {
    signerAddress?: string;
    accountLabel?: string;
    distributionMode: "stream" | "claim";
    rewardsDestination: string;
    tosAccepted: true;
    termsVersion: string;
    termsContentDigest: string;
}

export interface RetailMicroTransferDescriptor {
    recipient: string;
    /** Deployed API responses use strings; the local API contract also permits exact JSON integers. */
    lamports: string | number;
    memo: string;
}

export interface RetailWalletChallengeResponse {
    nonce: string;
    expiresAt: string;
    message: string;
    /** Absent until the additive backend rollout reaches this environment. */
    microTransfer?: RetailMicroTransferDescriptor;
}

export type RetailVerificationProof =
    | { method: "signMessage"; signature: string; signedMessage?: string }
    | { method: "microTransfer"; transactionSignature: string };

export type RetailVerificationMethod = RetailVerificationProof["method"];

export type RetailEnrollmentAccount = Omit<WalletParticipantAccount, "status" | "isRewardsEligible"> & {
    status: "Active";
    isRewardsEligible: boolean;
};

export interface RetailEnrollmentResponse {
    created: boolean;
    account: RetailEnrollmentAccount;
}

export type RetailEnrollmentErrorCode =
    | "validation_failed"
    | "payload_mismatch"
    | "signature_invalid"
    | "verification_invalid"
    | "verification_pending"
    | "terms_revision_mismatch"
    | "nonce_expired"
    | "nonce_consumed"
    | "account_pending"
    | "account_suspended"
    | "account_blacklisted"
    | "account_closed"
    | "rate_limited"
    | "internal_error"
    | "enrollment_unavailable";

export interface RetailEnrollmentErrorEnvelope {
    error: {
        code: RetailEnrollmentErrorCode;
        message: string;
        details?: Record<string, unknown>;
        requestId: string;
    };
}

/**
 * YaaS wallet response from API
 */
export type YaasWalletHolding = ((YaasLpHolding & { kind: "amm-position" }) | YaasTokenHolding) & {
    id: string;
    ownerAddress: string;
    fetchedAt: string;
};

export interface YaasWalletObservation {
    status: "partial" | "unavailable";
    source: "jachin";
    ownerAddress: string | null;
    complete: false;
    lpComplete: boolean;
    reason: "owner_enumeration_unavailable" | "discovery_unavailable";
    fetchedAt: string | null;
    configVersion: string | null;
    poolAddresses: string[];
    assetMints: string[];
}

export interface YaasWalletResponse {
    walletAddress: string;
    account: WalletParticipantAccount | null;
    holdings: YaasWalletHolding[];
    programPositions: YaasPosition[];
    observation: YaasWalletObservation;
}

/**
 * YaaS stats response from API
 */
export interface PublishedTermsDescriptor {
    termsUuid: string | null;
    name: string;
    documentKey: string;
    version: string;
    contentFormat: string;
    content: string;
    contentDigest: `sha256:${string}`;
}

export interface YaasStatsResponse {
    retailTermsStatus?: "available" | "unavailable";
    retailProgram?: {
        programUuid: string;
        programConfigVersionUuid: string;
        contentHash: string;
        enrollmentTerms: PublishedTermsDescriptor | null;
        termsRequirement: {
            mode: "ALL" | "ANY";
            existingParticipants: "KEEP_EXISTING" | "REQUIRE_CURRENT";
            terms: PublishedTermsDescriptor[];
        } | null;
    } | null;
    /** Published Retail USDv Rewards period expressed in days. */
    distributionDays?: number | null;
    /** The period the main-app Program is paying on (ISO). `nextPayoutAt` is
     *  the same value as the issuer console's Next Payout. Null unless the
     *  Program is Active and inside its duration. Optional while yaas-api
     *  and the apps deploy independently. */
    distributionSchedule?: {
        currentPeriodStartsAt: string;
        nextPayoutAt: string;
    } | null;
    /** Latest Jachin earning balance. Absent until the first successful sync. */
    flowSeries?: Array<{
        from: string;
        to: string;
        at: string;
        mintedVolume: string;
        mintedVolumeUi: string;
        redeemedVolume: string;
        redeemedVolumeUi: string;
    }>;
    mintedVolumeUi?: string | null;
    redeemedVolumeUi?: string | null;
    enrolledInEarnUi?: string | null;
    enrolledAsOf?: string | null;
    tokenMetricsFetchedAt?: string | null;
    id: number;
    totalTvl: string;
    totalRewardsDistributed: string;
    computedTotalTvl: string;
    computedTotalRewardsDistributed: string;
    /** Retail-program effective min-balance policy value; null when
     *  the retail program or rule is not configured. */
    minBalance: string | null;
    /** Retail-program effective min-distribution-threshold policy
     *  value; null when not configured. */
    minYieldPerDistribution: string | null;
    createdAt: string;
    updatedAt: string;
}

/**
 * Program Surface display info for UI
 */
export interface ProgramSurfaceDisplay {
    pair: string;
    protocol: string;
    type: string;
}

/**
 * Position display info for UI
 */
export interface YaasPositionDisplay {
    lpAddress: string;
    lpIdentity?: YaasLpPositionIdentity;
    /** Exact decimal value for portfolio math. null means no successful LP read yet. */
    usdvAmountExact?: string | null;
    /** The LP's USDv principal at yaas-api's last persisted observation, as
     *  a decimal. Absent before the first one. The fallback when the live
     *  wallet scan omits the LP, which proves nothing about the position. */
    lastObservedUsdvAmount?: string;
    USDvValue: number;
    formattedUSDvValue: string;
    apy: number;
    formattedApy: string;
    yaasApy: number;
    formattedYaasApy: string;
    /** Display status. "Inactive" is the neutral bucket for admin-side
     *  API states (Suspended / Rejected / Hidden / Ended) that retail
     *  users cannot act on — only API "Active" renders as "Earning". */
    status: "Enable" | "Pending" | "Earning" | "Disabled" | "Inactive";
    isActive: boolean;
}

/**
 * Rewards display info for UI
 */
export interface YaasRewardsDisplay {
    destination_address: string;
    rewards_amount: string;
}

/**
 * Position card data for UI
 */
export interface YaasPositionCardData {
    id: number;
    /** Stable public identifier used by the retail
     *  position-management endpoints. */
    uuid: string;
    pool: ProgramSurfaceDisplay;
    position: YaasPositionDisplay;
    rewards: YaasRewardsDisplay;
    splits: YaasPositionSplit[];
}

/**
 * Wallet stats for UI
 */
export interface YaasWalletStats {
    /** Exact calculation inputs. Optional for legacy demo fixtures; null means unknown. */
    totalPositionsExact?: BigDecimal | null;
    totalYieldEarnedExact?: BigDecimal;
    totalTvlExact?: BigDecimal;
    /** Numeric fields below are presentation projections only. */
    totalPositions: number;
    formattedTotalPositions: string;
    totalYieldEarned: number;
    formattedTotalYieldEarned: string;
    totalTvl: number;
    formattedTvl: string;
}

/**
 * Distribution pool info
 */
export interface DistributionPool {
    uuid: string;
    name: string;
    address: string;
    displayName: string;
}

/**
 * Distribution record
 */
export interface Distribution {
    id: number;
    distributionDate: string;
    amount: string;
    transactionId: string | null;
    balanceAtDistribution: string | null;
    createdAt: string;
    pool: DistributionPool;
}

/**
 * Distributions response from API
 */
export interface DistributionsResponse {
    data: Distribution[];
    total: number;
    limit: number;
    page: number;
}

/**
 * Balance history point
 */
export interface BalancePoint {
    snapshotDate: string;
    balance: string;
}

/**
 * Balance history response from API
 */
export interface BalanceHistoryResponse {
    data: BalancePoint[];
    total: number;
}
